// The bottom of a thread: a reply box while the 24 hour window is open, and a
// template picker once it has closed.
//
// The window is decided by the database (wa_conversations_v1), and sending
// text outside it is refused by the database too (wa_record_outbound_v1). This
// component only chooses which of the two to show, so the screen and the
// refusal cannot disagree about the rule. If the clock runs out while someone
// is typing, the send is refused with the same sentence shown here, and the
// draft is kept.
import { useEffect, useMemo, useState } from 'react'
import { RotateCw, Send } from 'lucide-react'
import { useGetTemplates, useSendMessage } from '../../hooks/backend/whatsapp'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { previewTemplate, type WaTemplate } from '../../utils/whatsapp'

export const WINDOW_CLOSED = 'The 24 hour window has closed. Only an approved template can be sent.'

export function Composer({
  phone,
  windowOpen,
  onSent,
}: {
  phone: string
  windowOpen: boolean
  onSent: () => void
}) {
  return windowOpen ? (
    <ReplyBox phone={phone} onSent={onSent} />
  ) : (
    <TemplatePicker phone={phone} onSent={onSent} />
  )
}

function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null
  return <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>
}

function ReplyBox({ phone, onSent }: { phone: string; onSent: () => void }) {
  const send = useSendMessage()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  // A draft belongs to one conversation. Switching threads starts clean.
  useEffect(() => {
    setText('')
    setError(null)
  }, [phone])

  const submit = async () => {
    if (!text.trim() || sending) return
    setSending(true)
    setError(null)
    try {
      await send.trigger({ phone, mode: 'text', text })
      setText('')
      onSent()
    } catch (e) {
      // The draft is kept on purpose. A refused message retyped from memory is
      // rarely the same message.
      setError(e instanceof Error ? e.message : String(e))
      onSent()
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-1.5 border-t border-border bg-card p-3">
      <div className="flex items-end gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              void submit()
            }
          }}
          placeholder="Type a reply"
          rows={2}
          maxLength={4096}
          className="min-h-[44px] flex-1 resize-y text-sm"
          aria-label="Reply"
        />
        <Button
          variant="brand"
          onClick={() => void submit()}
          disabled={!text.trim() || sending}
          className="h-10 shrink-0"
        >
          {sending ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
          <span className="ml-1.5 hidden sm:inline">Send</span>
        </Button>
      </div>
      <ErrorLine error={error} />
      <p className="text-[11px] text-muted-foreground">
        Ctrl or Cmd and Enter sends. Your name is recorded against the message.
      </p>
    </div>
  )
}

function TemplatePicker({ phone, onSent }: { phone: string; onSent: () => void }) {
  const templatesQuery = useGetTemplates<{ templates: WaTemplate[] }>()
  const send = useSendMessage()
  const [choice, setChoice] = useState('')
  const [values, setValues] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const loadTemplates = templatesQuery.trigger

  useEffect(() => {
    void loadTemplates({}, { skipCache: true }).catch(() => undefined)
  }, [loadTemplates])

  const all = templatesQuery.data?.templates ?? []
  const usable = all.filter((t) => t.usable)
  const unusable = all.filter((t) => !t.usable)
  const selected = useMemo(
    () => usable.find((t) => `${t.name}|${t.language}` === choice) ?? null,
    [usable, choice],
  )

  // Keyed on the choice, not on the template object, so a refetch of the
  // template list can never wipe fields somebody is halfway through filling.
  const fieldCount = selected?.variable_count ?? 0
  useEffect(() => {
    setValues(Array.from({ length: fieldCount }, () => ''))
    setError(null)
  }, [choice, fieldCount])

  const ready = selected && values.every((v) => v.trim() !== '')

  const submit = async () => {
    if (!selected || !ready || sending) return
    setSending(true)
    setError(null)
    try {
      await send.trigger({
        phone,
        mode: 'template',
        template_name: selected.name,
        language: selected.language,
        variables: values,
      })
      setChoice('')
      onSent()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      onSent()
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-2 border-t border-border bg-card p-3">
      <p className="text-xs font-medium text-amber-800 dark:text-amber-200">{WINDOW_CLOSED}</p>

      {templatesQuery.loading && !templatesQuery.data && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner className="h-3.5 w-3.5" /> Reading approved templates…
        </div>
      )}
      {templatesQuery.error && (
        <div className="flex items-start gap-2">
          <ErrorLine error={templatesQuery.error} />
          <button
            type="button"
            onClick={() => void loadTemplates({}, { skipCache: true }).catch(() => undefined)}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Try again"
          >
            <RotateCw className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {templatesQuery.data && usable.length === 0 && (
        <p className="text-xs text-muted-foreground">
          There is no approved template the inbox can send yet. Templates are created and approved in WhatsApp
          Manager.
          {unusable.length > 0 &&
            ` ${unusable.length} approved template${unusable.length === 1 ? ' needs' : 's need'} a header or button value the inbox cannot fill yet.`}
        </p>
      )}

      {usable.length > 0 && (
        <>
          <NativeSelect
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            className="h-9 text-sm"
            aria-label="Template"
          >
            <option value="">Pick an approved template</option>
            {usable.map((t) => (
              <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                {t.name} ({t.language}
                {t.variable_count ? `, ${t.variable_count} field${t.variable_count === 1 ? '' : 's'}` : ''})
              </option>
            ))}
          </NativeSelect>

          {selected && (
            <>
              {selected.variable_names.map((n, i) => (
                <label key={n} className="block space-y-1">
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {/^\d+$/.test(n) ? `Field ${n}` : n}
                  </span>
                  <Input
                    value={values[i] ?? ''}
                    onChange={(e) => {
                      const next = [...values]
                      next[i] = e.target.value
                      setValues(next)
                    }}
                    className="h-8 text-sm"
                  />
                </label>
              ))}
              <div className="rounded-md border border-dashed border-border bg-background p-2 text-xs whitespace-pre-wrap text-muted-foreground">
                {previewTemplate(selected, values)}
              </div>
              <div className="flex items-center justify-end">
                <Button variant="brand" onClick={() => void submit()} disabled={!ready || sending} size="sm">
                  {sending ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                  <span className="ml-1.5">Send template</span>
                </Button>
              </div>
            </>
          )}
          {unusable.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Not shown: {unusable.map((t) => `${t.name} (${t.unusable_reason})`).join('; ')}
            </p>
          )}
        </>
      )}
      <ErrorLine error={error} />
    </div>
  )
}
